import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  IconButton,
  Alert,
  Collapse,
  Divider,
  FormControlLabel,
  Switch,
  Tooltip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import CheckIcon from "@mui/icons-material/Check";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import { hostname } from "../utils";
import LoadingButton from "./LoadingButton";

interface AITextAssistantDialogProps {
  open: boolean;
  onClose: () => void;
  onAccept: (generatedText: string) => void;
  existingText: string;
  fieldContext: string;
  getToken: () => Promise<string>;
  companyId: string;
}

const AITextAssistantDialog: React.FC<AITextAssistantDialogProps> = ({
  open,
  onClose,
  onAccept,
  existingText,
  fieldContext,
  getToken,
  companyId,
}) => {
  const [userRequest, setUserRequest] = useState("");
  const [generatedText, setGeneratedText] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [includeCompanyContext, setIncludeCompanyContext] = useState(true);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setUserRequest("");
      setGeneratedText("");
      setError(null);
      setHasGenerated(false);
      setIncludeCompanyContext(true);
    }
  }, [open]);

  const handleGenerate = async () => {
    if (!userRequest.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const token = await getToken();
      const response = await axios.post(
        `${hostname}/completions/text-assistant`,
        {
          userRequest: userRequest.trim(),
          existingText: existingText,
          fieldContext: fieldContext,
          companyId: companyId,
          includeCompanyContext: includeCompanyContext,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setGeneratedText(response.data.content);
      setHasGenerated(true);
    } catch (err: any) {
      const errorMessage =
        err.response?.data?.error || err.message || "Failed to generate text";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleAccept = () => {
    onAccept(generatedText);
    onClose();
  };

  const handleRegenerate = () => {
    handleGenerate();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AutoAwesomeIcon color="primary" />
            <Typography variant="h6">AI Writing Assistant</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        {fieldContext && (
          <Alert severity="info" sx={{ mb: 2 }}>
            <Typography variant="body2">
              <strong>Context:</strong> {fieldContext}
            </Typography>
          </Alert>
        )}

        {existingText && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 0.5 }}
            >
              Current content (will be used as context):
            </Typography>
            <Box
              sx={{
                p: 1.5,
                bgcolor: "action.hover",
                borderRadius: 1,
                maxHeight: 120,
                overflow: "auto",
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: "pre-wrap",
                  fontFamily: "monospace",
                  fontSize: "0.8rem",
                }}
              >
                {existingText.length > 500
                  ? existingText.substring(0, 500) + "..."
                  : existingText}
              </Typography>
            </Box>
          </Box>
        )}

        <TextField
          autoFocus
          fullWidth
          multiline
          rows={3}
          label="What would you like the AI to do?"
          placeholder={
            existingText
              ? "e.g., Make this more concise, Add more detail about X, Rewrite in a professional tone..."
              : "e.g., Write a system prompt for a customer support bot, Create an email template for..."
          }
          value={userRequest}
          onChange={(e) => setUserRequest(e.target.value)}
          disabled={isLoading}
          sx={{ mb: 2 }}
        />

        <Tooltip title="Include your company name, website, and description to help AI write more relevant content">
          <FormControlLabel
            control={
              <Switch
                checked={includeCompanyContext}
                onChange={(e) => setIncludeCompanyContext(e.target.checked)}
                disabled={isLoading}
              />
            }
            label={
              <Typography variant="body2">Include company context</Typography>
            }
            sx={{ mb: 2 }}
          />
        </Tooltip>

        {!hasGenerated && (
          <LoadingButton
            variant="contained"
            onClick={handleGenerate}
            loading={isLoading}
            disabled={!userRequest.trim()}
            startIcon={<AutoAwesomeIcon />}
            fullWidth
          >
            Generate with AI
          </LoadingButton>
        )}

        <Collapse in={hasGenerated}>
          <Divider sx={{ my: 2 }} />

          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ mb: 1 }}
          >
            Generated Result:
          </Typography>

          {error ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          ) : (
            <Box
              sx={{
                p: 2,
                bgcolor: "background.paper",
                border: 1,
                borderColor: "primary.main",
                borderRadius: 1,
                maxHeight: 300,
                overflow: "auto",
                mb: 2,
              }}
            >
              <Typography
                variant="body1"
                sx={{
                  whiteSpace: "pre-wrap",
                  fontFamily: "inherit",
                }}
              >
                {generatedText}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={handleRegenerate}
              disabled={isLoading || !userRequest.trim()}
              startIcon={isLoading ? undefined : <RefreshIcon />}
            >
              {isLoading ? "Regenerating..." : "Regenerate"}
            </Button>
            <Button
              variant="contained"
              onClick={handleAccept}
              disabled={!generatedText || isLoading}
              startIcon={<CheckIcon />}
            >
              Use This Text
            </Button>
          </Box>
        </Collapse>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AITextAssistantDialog;
